import {
  ActionEvents,
  CreateTask,
  ID,
  IDPrefixes,
  MinimalRelationship,
  NewGoalWithID,
  NewTagWithID,
  NewTask,
  Task,
  TaskActionType,
  UpdateGoal,
  UpdateLifeArea,
} from '@griply/types';
import { createPrefixId, taskTypeToTrackingTaskType } from '@griply/utils';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useCallback } from 'react';
import { NewSubTask } from 'shared/types/task';

import { useCreateGoalsWithIdsMutation } from './queries/use-create-goals-with-ids-mutation';
import { useCreateRelationshipsMutation } from './queries/use-create-relationships-mutation';
import { useCreateTagsWithIdsMutation } from './queries/use-create-tags-with-ids-mutation';
import { useCreateTasksWithSubTasksMutation } from './queries/use-create-tasks-with-sub-tasks-mutation';
import { useUpdateGoalMutation } from './queries/use-update-goal-mutation';
import { useUpdateLifeAreaMutation } from './queries/use-update-life-area-mutation';
import { useUpdateRelationshipsReferencesMutation } from './queries/use-update-relationships-references-mutation';
import { useRelationships } from './use-relationships';
import { useTrackEvents } from './use-track-events';

export type BulkCreateOptions = {
  tasks: CreateTask[];
  subTasks?: NewSubTask[];
  goalsToCreate?: NewGoalWithID[];
  tagsToCreate?: NewTagWithID[];
};

const aggregateGoalUpdates = (tasks: CreateTask[], createdTasks: Task[]) => {
  const goalUpdates = new Map<
    ID,
    { taskIds: ID[]; sectionId: ID | null; hasGoalTimeslot: boolean }
  >();

  for (const [index, createdTask] of createdTasks.entries()) {
    if (!createdTask.goalId) {
      continue;
    }

    const sectionId = tasks[index].sectionId;
    const existing = goalUpdates.get(createdTask.goalId);

    if (existing) {
      existing.taskIds.push(createdTask.id);
      if (!existing.hasGoalTimeslot) {
        existing.hasGoalTimeslot =
          createdTask.action?.type === TaskActionType.GoalTimeslot;
      }
    } else {
      goalUpdates.set(createdTask.goalId, {
        taskIds: [createdTask.id],
        sectionId,
        hasGoalTimeslot:
          createdTask.action?.type === TaskActionType.GoalTimeslot,
      });
    }
  }

  return goalUpdates;
};

const aggregateLifeAreaUpdates = (
  tasks: CreateTask[],
  createdTasks: Task[],
) => {
  const lifeAreaUpdates = new Map<ID, { taskIds: ID[]; sectionId: ID }>();

  for (const [index, createdTask] of createdTasks.entries()) {
    const sectionId = tasks[index].sectionId;
    if (!createdTask.lifeAreaId || !sectionId) {
      continue;
    }

    const existing = lifeAreaUpdates.get(createdTask.lifeAreaId);
    if (existing) {
      existing.taskIds.push(createdTask.id);
    } else {
      lifeAreaUpdates.set(createdTask.lifeAreaId, {
        taskIds: [createdTask.id],
        sectionId,
      });
    }
  }

  return lifeAreaUpdates;
};

const aggregateRelationshipUpdates = (
  tasks: CreateTask[],
  createdTasks: Task[],
  tagIdMap: Record<ID, ID>,
  existingRelationships: MinimalRelationship[],
) => {
  const relationshipsToUpdate: MinimalRelationship[] = [];
  const relationshipsToCreate: MinimalRelationship[] = [];

  for (const [index, createdTask] of createdTasks.entries()) {
    const { tagIds } = tasks[index];

    tagIds?.forEach((tagId) => {
      const _tagId = tagIdMap[tagId] ?? tagId;
      const prefixedId = createPrefixId(_tagId, IDPrefixes.Tag);
      const taskRef = createPrefixId(createdTask.id, IDPrefixes.Task);

      // check local lists first (handles tasks sharing the same tag)
      let relationship =
        relationshipsToUpdate.find(({ id }) => id === prefixedId) ??
        relationshipsToCreate.find(({ id }) => id === prefixedId);

      if (!relationship) {
        // fall back to existing relationships (pre-existing)
        const existing = existingRelationships.find(
          ({ id }) => id === prefixedId,
        );
        relationship = existing
          ? { id: existing.id, references: [...existing.references] }
          : { id: prefixedId, references: [] };

        const list = existing ? relationshipsToUpdate : relationshipsToCreate;
        list.push(relationship);
      }

      relationship.references = Array.from(
        new Set([...relationship.references, taskRef]),
      );
    });
  }

  return { relationshipsToCreate, relationshipsToUpdate };
};

export const useCreateTasksWithEntities = () => {
  const track = useTrackEvents();

  const { mutateAsync: createTasks } = useCreateTasksWithSubTasksMutation();
  const { mutateAsync: createGoals } = useCreateGoalsWithIdsMutation();
  const { mutateAsync: updateGoal } = useUpdateGoalMutation();
  const { mutateAsync: updateLifeArea } = useUpdateLifeAreaMutation();
  const { mutateAsync: createTags } = useCreateTagsWithIdsMutation();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const relationships = useRelationships();
  const { mutateAsync: createRelationships } = useCreateRelationshipsMutation();
  const { mutateAsync: updateRelationships } =
    useUpdateRelationshipsReferencesMutation();

  return useCallback(
    async ({
      tasks,
      subTasks,
      tagsToCreate,
      goalsToCreate,
    }: BulkCreateOptions) => {
      // collect all tagIds across tasks for entity creation
      const allTagIds = tasks.flatMap(({ tagIds }) => tagIds ?? []);

      // create goal once if needed (all bulk tasks share the same base properties)
      const goalToCreate = goalsToCreate?.find(({ id }) =>
        tasks.some(({ goalId }) => goalId === id),
      );
      let resolvedGoalId: ID | null = null;
      if (goalToCreate) {
        const { goals: createdGoals } = await createGoals([
          { ...goalToCreate, taskCount: tasks.length },
        ]);
        resolvedGoalId = createdGoals[0].id;
      }

      // create tags once if needed
      const _tagsToCreate = tagsToCreate?.filter(({ id }) =>
        allTagIds.includes(id),
      );

      const { oldIdToNewIdMap: tagIdMap } = _tagsToCreate
        ? await createTags(_tagsToCreate)
        : { oldIdToNewIdMap: {} as Record<ID, ID> };

      // build task entries for bulk creation
      const taskEntries = tasks.map((task) => {
        const { tagIds: _tagIds, ...taskWithoutTags } = task;
        return {
          task: {
            ...taskWithoutTags,
            goalId: resolvedGoalId ?? taskWithoutTags.goalId,
            startDate: new Date(),
            createdAt: new Date(),
            endStrategy: task.endStrategy
              ? {
                  deadline: task.endStrategy?.deadline ?? null,
                  completionCount: task.endStrategy?.completionCount ?? null,
                }
              : null,
          } as NewTask,
          subTasks: subTasks ?? [],
        };
      });

      // create all tasks + subtasks in one batch
      const createdTasks = await createTasks({ taskEntries });

      // update goals, life areas, and tag relationships in parallel
      // (each targets a different entity, so no stale state conflicts)
      const metaPromises: Promise<void>[] = [];
      if (!goalToCreate) {
        const goalUpdates = aggregateGoalUpdates(tasks, createdTasks);

        for (const [
          goalId,
          { taskIds, sectionId, hasGoalTimeslot },
        ] of goalUpdates) {
          const goalToUpdate = goals.find(({ id }) => id === goalId);
          const goalBase: UpdateGoal = { id: goalId };

          if (sectionId && goalToUpdate) {
            goalBase.sections = goalToUpdate.sections?.map((section) => {
              if (section.id === sectionId) {
                return {
                  ...section,
                  tasks: Array.from(new Set([...section.tasks, ...taskIds])),
                };
              }
              return section;
            });
          }

          if (!hasGoalTimeslot) {
            goalBase.taskCount = taskIds.length;
          }

          metaPromises.push(updateGoal({ goal: goalBase }));
        }
      }

      const lifeAreaUpdates = aggregateLifeAreaUpdates(tasks, createdTasks);

      for (const [lifeAreaId, { taskIds, sectionId }] of lifeAreaUpdates) {
        const lifeAreaToUpdate = lifeAreas.find(({ id }) => id === lifeAreaId);
        if (!lifeAreaToUpdate) {
          continue;
        }

        const lifeAreaBase: UpdateLifeArea = { id: lifeAreaId };
        lifeAreaBase.sections = lifeAreaToUpdate.sections?.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              tasks: Array.from(new Set([...section.tasks, ...taskIds])),
            };
          }
          return section;
        });

        if (lifeAreaBase.sections) {
          metaPromises.push(updateLifeArea(lifeAreaBase));
        }
      }

      const { relationshipsToCreate, relationshipsToUpdate } =
        aggregateRelationshipUpdates(
          tasks,
          createdTasks,
          tagIdMap,
          relationships,
        );

      if (relationshipsToCreate.length) {
        metaPromises.push(createRelationships(relationshipsToCreate));
      }
      if (relationshipsToUpdate.length) {
        metaPromises.push(updateRelationships(relationshipsToUpdate));
      }

      await Promise.all(metaPromises);

      // track the creation of each task
      for (const createdTask of createdTasks) {
        track(ActionEvents.TaskCreate, {
          type: taskTypeToTrackingTaskType[createdTask.type],
          deadline_added: !!createdTask.endStrategy?.deadline,
          linked_goal: !!createdTask.goalId,
        });
      }

      return createdTasks;
    },
    [
      createGoals,
      createRelationships,
      createTags,
      createTasks,
      goals,
      lifeAreas,
      relationships,
      track,
      updateGoal,
      updateLifeArea,
      updateRelationships,
    ],
  );
};
